import { useCallback, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { navigate } from 'gatsby';
import { useSessionStore } from '../store/session';
import { useChannelStore } from '../store/channel';
import { useCustomerStore } from '../store/customer';
import { useUIStore } from '../store/ui';
import { useRechargeStore } from '../store/recharge';
import { useRecurrenceStore } from '../store/recurrence';
import { useLastRechargeStore } from '../store/lastRecharge';
import { useCheckoutStore } from '../store/checkout';
import { useMsisdnChangeStore } from '../store/msisdnChange';
import { useRecharge } from './useRecharge';
import { setUnauthorizedHandler } from '../services/api';
import sessionService from '../services/sessionService';
import customerService from '../services/customerService';
import { handleError, isCustomerBlockedError } from '../lib/errors/handleError';
import {
  sendCreateSessionSuccess,
  sendCreateSessionError,
} from '../services/timPostMessages';
import {
  TIM_INCENTIVO_PROGRAMADA,
  TIM_WEBMOB,
  PIX_CHANNELS,
  TIM_FALLBACK_SMS,
  TIM_URA,
  TIM_LEMBRETE_PIX_NAO_PAGO,
  TIM_TROCA_MSISDN,
} from '../constants/channels';

const CUSTOMER_BLOCKED_PATH = '/web/canal-exclusivo';

const afterLogin = async ({
  msisdn,
  channel,
  rechargeValue,
  pixExternalId,
  authCode,
  setChannel,
  setSessionLoopId,
  setCustomer,
  setFeatureToggles,
  createPixRecharge,
  setPixTransactionId,
  setPixRechargeValue,
  showError,
  logout,
}) => {
  // Passo 3/4 — busca dados do cliente e feature toggles em paralelo
  // (nenhuma das duas depende do resultado da outra, só do msisdn já
  // conhecido — cada uma trata seu próprio erro para não fazer a falha de
  // uma cancelar o resultado da outra)
  const [customerData, featureTogglesData] = await Promise.all([
    customerService
      .getCustomer(msisdn)
      .then(({ data }) => {
        setCustomer(data);
        return data;
      })
      .catch(err => {
        handleError(err, showError, logout);
        return undefined;
      }),
    customerService
      .getFeatureToggles(msisdn)
      .then(({ data }) => {
        setFeatureToggles(data);
        return data;
      })
      .catch(err => {
        handleError(err, showError, logout);
        return undefined;
      }),
  ]);

  if (featureTogglesData?.maintenance) {
    navigate('/web/maintenance', { replace: true });
    return;
  }

  // Passo 6 — sessionLoopId para envio de eventos ao loop. No autologado com
  // auth_code, reaproveita o próprio auth_code para correlacionar os eventos
  // com a sessão originada pela operadora; nos outros fluxos, gera um UUID.
  setSessionLoopId(authCode || uuidv4());

  // Passo 7 — PIX: transactionId vem de fontes diferentes por canal
  if (channel === TIM_FALLBACK_SMS && rechargeValue > 0) {
    await createPixRecharge(rechargeValue, msisdn);
  } else if (
    [TIM_URA, TIM_LEMBRETE_PIX_NAO_PAGO].includes(channel) &&
    pixExternalId
  ) {
    setPixTransactionId(pixExternalId);
    setPixRechargeValue(rechargeValue);
  }

  // Passo 8 — decide para qual página navegar conforme canal
  if (PIX_CHANNELS.includes(channel)) {
    navigate('/web/confirmacao-recarga-avulsa', { replace: true });
  } else if (channel === TIM_INCENTIVO_PROGRAMADA) {
    const isEligible = customerData?.credit_cards?.length > 0;
    if (isEligible) {
      navigate('/web/programar-recarga', { replace: true });
    } else {
      setChannel(TIM_WEBMOB);
      navigate('/web/valores-recarga', { replace: true });
    }
  } else if (channel === TIM_TROCA_MSISDN) {
    navigate('/web/troca-numero', { replace: true });
  } else {
    navigate('/web/valores-recarga', { replace: true });
  }
};

export const useSession = () => {
  const {
    msisdn,
    sessionLoopId,
    upsellPlan,
    setSession,
    setMsisdn,
    setSessionLoopId,
    setUpsellPlan,
    clearSession,
  } = useSessionStore();
  const { channel, setChannel } = useChannelStore();
  const { setCustomer, setFeatureToggles, clearCustomer } = useCustomerStore();
  const { clearRecharge } = useRechargeStore();
  const { clearRecurrence } = useRecurrenceStore();
  const { clearLastRecharge } = useLastRechargeStore();
  const { clearCheckout } = useCheckoutStore();
  const { clearMsisdnChange } = useMsisdnChangeStore();
  const { showError, showLoading, hideLoading } = useUIStore();
  const { createPixRecharge, setPixTransactionId, setPixRechargeValue } =
    useRecharge();

  const clearJourney = useCallback(() => {
    clearSession();
    clearCustomer();
    clearRecharge();
    clearRecurrence();
    clearLastRecharge();
    clearCheckout();
    clearMsisdnChange();
    setChannel(null);
  }, [
    clearSession,
    clearCustomer,
    clearRecharge,
    clearRecurrence,
    clearLastRecharge,
    clearCheckout,
    clearMsisdnChange,
    setChannel,
  ]);

  const logout = useCallback(() => {
    clearJourney();
    localStorage.clear();
    navigate('/web/login');
  }, [clearJourney]);

  // O msisdn gravado no envio do SMS basta para o AuthGuard liberar as rotas
  // protegidas, e sem sessionId o 401 não derruba ninguém: a jornada é limpa
  // antes de ir para a tela de bloqueio.
  const blockCustomer = useCallback(() => {
    clearJourney();
    navigate(CUSTOMER_BLOCKED_PATH);
  }, [clearJourney]);

  useEffect(() => {
    setUnauthorizedHandler(logout);
  }, [logout]);

  const sendSmsToken = useCallback(
    async (phoneNumber: string) => {
      showLoading();
      try {
        await sessionService.sendSmsToken(phoneNumber);
        setMsisdn(phoneNumber);
        navigate('/web/sms');
      } catch (err) {
        handleError(err, showError);
      } finally {
        hideLoading();
      }
    },
    [setMsisdn, showError, showLoading, hideLoading],
  );

  const verifySmsToken = useCallback(
    async (token: string) => {
      showLoading();
      try {
        const { data } = await sessionService.verifySmsToken(msisdn, token);
        setSession({ sessionId: data.id, msisdn });
        await afterLogin({
          msisdn,
          channel,
          rechargeValue: 0,
          pixExternalId: null,
          authCode: null,
          setChannel,
          setSessionLoopId,
          setCustomer,
          setFeatureToggles,
          createPixRecharge,
          setPixTransactionId,
          setPixRechargeValue,
          showError,
          logout,
        });
      } catch (err) {
        if (isCustomerBlockedError(err)) {
          blockCustomer();
          return;
        }
        handleError(err, showError);
      } finally {
        hideLoading();
      }
    },
    [
      msisdn,
      channel,
      setChannel,
      setSession,
      setSessionLoopId,
      setCustomer,
      setFeatureToggles,
      showError,
      showLoading,
      hideLoading,
      logout,
      blockCustomer,
    ],
  );

  const verifyQueryStringToken = useCallback(
    async ({
      token,
      authCode,
      silent = false,
    }: {
      token: string;
      authCode: string;
      silent?: boolean;
    }) => {
      if (!silent) showLoading();
      try {
        const { data } = await sessionService.verifyQueryStringToken({
          token,
          authCode,
        });
        const resolvedMsisdn = data.msisdn;
        const rechargeValue = data.rechargeValue?.id || 0;
        const pixExternalId = data.pixExternalId || null;
        const resolvedUpsellPlan = data.sessionData?.upsellPlan ?? true;
        const currentChannel = useChannelStore.getState().channel;
        setSession({ sessionId: data.id, msisdn: resolvedMsisdn });
        setUpsellPlan(resolvedUpsellPlan);
        sendCreateSessionSuccess({ authCode, channel: currentChannel });
        await afterLogin({
          msisdn: resolvedMsisdn,
          channel: currentChannel,
          rechargeValue,
          pixExternalId,
          authCode,
          setChannel,
          setSessionLoopId,
          setCustomer,
          setFeatureToggles,
          createPixRecharge,
          setPixTransactionId,
          setPixRechargeValue,
          showError,
          logout,
        });
      } catch (err) {
        sendCreateSessionError({
          authCode,
          err,
          channel: useChannelStore.getState().channel,
        });
        // Bloqueio não é falha de login: a tela própria substitui o modal e o
        // estado de erro das páginas de entrada, por isso não relança.
        if (isCustomerBlockedError(err)) {
          blockCustomer();
          return;
        }
        handleError(err, showError);
        throw err;
      } finally {
        if (!silent) hideLoading();
      }
    },
    [
      setSession,
      setUpsellPlan,
      setSessionLoopId,
      setCustomer,
      setFeatureToggles,
      createPixRecharge,
      setPixTransactionId,
      setPixRechargeValue,
      showError,
      showLoading,
      hideLoading,
      logout,
      blockCustomer,
    ],
  );

  return {
    msisdn,
    sessionLoopId,
    upsellPlan,
    setUpsellPlan,
    channel,
    logout,
    sendSmsToken,
    verifySmsToken,
    verifyQueryStringToken,
  };
};
